import produce from 'immer';

/**
 * Dedupes the unit allotments of a given department and
 * merges the allotment data of duplicate entries into a single entry
 * @param department
 * @returns {{unitAllotments: *[]}}
 */
export const dedupeAndMergeUnitAllotments = department => {
  if (!Array.isArray(department.unitAllotments) || department.unitAllotments.length === 0) {
    return department;
  }
  // Temporary object to store duplicate entries by using a generated ID as a unique key
  const unitAllotments = {};

  department.unitAllotments.forEach(allotment => {
    const { allotmentAmount, description, totalSpend, unitAttributeName, unitAttributeValue } = allotment;
    const uniqueId = `${unitAttributeName}${unitAttributeValue}`;

    // Check for existing unitAllotment by generated ID
    if (unitAllotments[uniqueId]) {
      // Combine Allotment Amounts
      unitAllotments[uniqueId].allotmentAmount =
        Number(unitAllotments[uniqueId].allotmentAmount) + Number(allotmentAmount);

      // Combine Total Spend
      unitAllotments[uniqueId].totalSpend = Number(unitAllotments[uniqueId].totalSpend) + Number(totalSpend);
    } else {
      // No Allotment with generated ID exists. Make one.
      unitAllotments[uniqueId] = {
        allotmentAmount,
        description,
        totalSpend,
        uniqueId,
        unitAttributeName,
        unitAttributeValue
      };
    }
  });

  return {
    ...department,
    // Convert temporary object back into an Array
    unitAllotments: Object.keys(unitAllotments).map(key => unitAllotments[key])
  };
};

/**
 * updateAllotmentDepts
 * @param {Object} depts allotment departments
 * @return {Object} updated allotment departments object w/ deduped unitAllotments
 * and deduped unitAllotments
 */
export const updateAllotmentDepts = (depts = {}) =>
  Object.entries(depts).reduce(
    (acc, [departmentNumber, dept]) => ({
      ...acc,
      [departmentNumber]: dedupeAndMergeUnitAllotments(dept)
    }),
    {}
  );

/**
 * updateAccountData
 * @param {Object} gamedayAccount
 * @return {Object} gamedayAccount with adjusted allotment dept format/data
 */
export const updateAccountData = gamedayAccount =>
  produce(gamedayAccount, draftAccount => {
    const { currentAllotment: current, futureAllotment: future } = draftAccount.allotments;
    if (current?.departments) {
      current.departments = updateAllotmentDepts(current.departments);
    }
    if (future?.departments) {
      future.departments = updateAllotmentDepts(future.departments);
    }
  });
